/**
 * Bemka header behaviour.
 *
 * Three custom elements, no framework, no dependencies:
 *
 *   <sticky-header>  compact-on-scroll state for the header bar
 *   <header-nav>     hover/focus dropdowns for the desktop main menu
 *   <header-modal>   the mobile drawer and the search overlay
 *
 * Everything degrades: with JavaScript disabled the header still renders and
 * every top-level menu link, the cart, the account link and the search page
 * link remain reachable. Desktop dropdowns additionally open on plain CSS
 * :hover, so a failed script only costs keyboard users the submenu.
 */
(function () {
  'use strict';

  /* The React storefront used an asymmetric pair of thresholds so the header
     does not flicker between states while the user hovers around the top of
     the page: it goes compact only after 50px of downward scroll, and comes
     back only once we are within 10px of the very top. Ported verbatim. */
  var SCROLL_COMPACT_AT = 50;
  var SCROLL_EXPAND_AT = 10;

  var BODY_MODAL_CLASS = 'header-modal-open';

  function isEscape(event) {
    return event.key === 'Escape' || event.key === 'Esc';
  }

  /* ---------------------------------------------------------------- sticky */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.compact = false;

      var sticky = this.dataset.sticky === 'true';
      var shrink = this.dataset.shrink === 'true';

      /* Shrinking a header that does not follow the scroll would just make the
         bar jump as the page moves, so the two settings are ANDed here. */
      if (!sticky || !shrink) return;

      /* Off the home page the source rendered the header in its compact state
         immediately — there is no tall hero to sit over, so the full-height
         bar would only waste viewport. No scroll listener is needed then. */
      if (this.dataset.startCompact === 'true') {
        this.setCompact(true);
        return;
      }

      this.handleScroll = this.handleScroll.bind(this);
      this.handleScroll();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    }

    handleScroll() {
      var y = window.scrollY;
      if (!this.compact && y > SCROLL_COMPACT_AT) this.setCompact(true);
      else if (this.compact && y < SCROLL_EXPAND_AT) this.setCompact(false);
    }

    setCompact(next) {
      this.compact = next;
      this.classList.toggle('is-scrolled', next);
    }
  }

  /* ------------------------------------------------------------------- nav */

  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.prototype.slice.call(
        this.querySelectorAll('[data-nav-item]')
      );
      if (!this.items.length) return;

      this.closeAll = this.closeAll.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);

      this.items.forEach(function (item) {
        var toggle = item.querySelector('[data-nav-toggle]');

        item.addEventListener('mouseenter', this.open.bind(this, item));
        item.addEventListener('mouseleave', this.close.bind(this, item));

        /* Open on focus so a keyboard user tabbing along the menu bar sees the
           same thing a mouse user sees on hover. */
        item.addEventListener('focusin', this.open.bind(this, item));

        item.addEventListener(
          'focusout',
          function (event) {
            if (!item.contains(event.relatedTarget)) this.close(item);
          }.bind(this)
        );

        item.addEventListener(
          'keydown',
          function (event) {
            if (!isEscape(event)) return;
            /* Escape closes this one dropdown and puts focus back on its
               disclosure button — nothing is trapped, so the next Tab
               continues along the menu bar as usual. */
            event.stopPropagation();
            this.close(item);
            if (toggle) toggle.focus();
          }.bind(this)
        );

        if (toggle) {
          toggle.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              if (item.hasAttribute('data-open')) this.close(item);
              else this.open(item);
            }.bind(this)
          );
        }
      }, this);

      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.closeAll();
    }

    open(item) {
      this.items.forEach(function (other) {
        if (other !== item) this.close(other);
      }, this);

      item.setAttribute('data-open', '');
      var toggle = item.querySelector('[data-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    close(item) {
      item.removeAttribute('data-open');
      var toggle = item.querySelector('[data-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.items.forEach(function (item) {
        this.close(item);
      }, this);
    }
  }

  /* ----------------------------------------------------------------- modal */

  /**
   * The mobile drawer and the search overlay are the same object: a panel that
   * is opened by one or more buttons pointing at it with `aria-controls`,
   * closed by its overlay, its close button or Escape, and which hands focus
   * back to whichever button opened it.
   *
   * Focus is moved INTO the panel on open (to `[data-modal-focus]` when the
   * panel names one — the search field — otherwise to the close button) but is
   * deliberately not trapped: Tab eventually leaves the panel, which is the
   * behaviour a user of a small overlay generally expects and which cannot
   * strand anyone if the markup changes.
   */
  class HeaderModal extends HTMLElement {
    connectedCallback() {
      this.opener = null;
      this.handleKeydown = this.handleKeydown.bind(this);

      this.triggers = Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + this.id + '"]')
      );

      this.triggers.forEach(function (trigger) {
        trigger.addEventListener(
          'click',
          function (event) {
            event.preventDefault();
            this.open(trigger);
          }.bind(this)
        );
      }, this);

      Array.prototype.forEach.call(
        this.querySelectorAll('[data-modal-close]'),
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      this.addEventListener(
        'click',
        function (event) {
          if (event.target.hasAttribute('data-modal-overlay')) this.close();
        }.bind(this)
      );
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove(BODY_MODAL_CLASS);
    }

    handleKeydown(event) {
      if (isEscape(event)) this.close();
    }

    open(trigger) {
      this.opener = trigger || null;
      this.setAttribute('open', '');
      document.body.classList.add(BODY_MODAL_CLASS);
      document.addEventListener('keydown', this.handleKeydown);

      this.triggers.forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });

      var target =
        this.querySelector('[data-modal-focus]') ||
        this.querySelector('[data-modal-close]');
      if (target) target.focus();
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove(BODY_MODAL_CLASS);
      document.removeEventListener('keydown', this.handleKeydown);

      this.triggers.forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });

      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }
  if (!customElements.get('header-nav')) {
    customElements.define('header-nav', HeaderNav);
  }
  if (!customElements.get('header-modal')) {
    customElements.define('header-modal', HeaderModal);
  }
})();
